import React from 'react';
import { toNumber } from 'lodash';
import { Link } from 'react-router-dom';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import DownloadIcon from '@atlaskit/icon/glyph/download';
import Lozenge from '@atlaskit/lozenge';
import { InvoiceStatus, Refunds } from '@css-xp/graphtypes/src/generated-types-new';
import { PaymentErrorLozenge, RefundedLozenge } from '@css-xp/base/copyable-text';
import { PdfRedirectButton } from '@css-xp/billing-v2/invoices/src/invoice-page-utils';
import './invoice-cell.scss';

interface InvoiceCellProps {
  invoiceNumber: string;
  status?: string | undefined | null;
  paymentError?: boolean | undefined;
  invoiceId?: string;
  transactionAccountId?: string;
  grandTotal: string | undefined | null | number;
  refunds?: Refunds[];
  invoiceLink: string | undefined;
}

const hasPaymentIssues = ({ status, paymentIssue }: { status?: string | null; paymentIssue?: boolean }) => {
  const PAYMENT_ERROR_STATUS: string[] = [
    InvoiceStatus.OPEN,
    InvoiceStatus.DRAFT,
    InvoiceStatus.PAYMENT_DEFERRED,
    InvoiceStatus.VOID,
  ];
  return paymentIssue && status && PAYMENT_ERROR_STATUS.includes(status);
};

export const isPaymentIssues = (status: string | undefined | null, paymentIssue = false) => {
  return hasPaymentIssues({ status, paymentIssue }) ? <PaymentErrorLozenge /> : undefined;
};

const negativeInvoiceStatus = (grandTotal: string | undefined | null | number) => {
  if (grandTotal && toNumber(grandTotal) <= 0) {
    return (
      <span className="invoice-lozenge">
        <Lozenge testId="negative-status-lozenge">NEGATIVE</Lozenge>
      </span>
    );
  }
  return null;
};

export const InvoiceCell = ({
  invoiceNumber,
  status,
  paymentError,
  transactionAccountId,
  invoiceId,
  grandTotal,
  refunds,
  invoiceLink,
}: InvoiceCellProps): JSX.Element => {
  const invoiceSlug = invoiceNumber || (invoiceId ? invoiceId : '');
  return (
    <div className="invoice-container" data-testid="invoice-container" onClick={(e) => e.stopPropagation()}>
      <PdfRedirectButton href={''} label="View PDF" invoiceId={invoiceId} transactionAccountId={transactionAccountId}>
        <ToolTip content={<>Download invoice</>} position="bottom-start" tag="span">
          <DownloadIcon
            aria-label="Download invoice PDF"
            label="Download invoice PDF"
            size="medium"
            testId="download-Icon"
            primaryColor={token('color.icon')}
          />
        </ToolTip>
      </PdfRedirectButton>

      <Link to={invoiceLink} className="invoice-link" data-testid="invoice-link">
        <>{invoiceSlug}</>
      </Link>
      <div>
        {negativeInvoiceStatus(grandTotal)}
        {isPaymentIssues(status, paymentError)}
        {refunds && refunds.length > 0 && <RefundedLozenge />}
      </div>
    </div>
  );
};
